/**
 * Shared parsing for the canonical `hero` object in locale JSON (`home.json`,
 * `design-system.json`, `portfolio.json`, etc.). See `src/components/docs/HERO-LOCALE.md`.
 */
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';

export type LayoutLocale = 'en' | 'es';

export type HeroImage = {
  id: number;
  title: string;
  image_url: string;
  image_url_2x?: string;
};

export type HeroCtaEntry = { hero_cta: string; hero_cta_url: string };

export function isHeroImage(value: unknown): value is HeroImage {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'number' &&
    typeof v.title === 'string' &&
    typeof v.image_url === 'string' &&
    (typeof v.image_url_2x === 'string' || typeof v.image_url_2x === 'undefined')
  );
}

export function isHeroCtaEntry(value: unknown): value is HeroCtaEntry {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.hero_cta === 'string' && typeof v.hero_cta_url === 'string';
}

/** Prefix internal paths with `/es` when the shell is Spanish (matches root `template` / `layout`). */
export function resolveHeroCtaHref(href: string, locale: LayoutLocale): string {
  if (!href.startsWith('/') || href.startsWith('/es')) return href;
  if (locale === 'es') return '/es' + (href === '/' ? '' : href);
  return href;
}

/** Scalar line under `hero.*` (rich-string aware). */
export function heroLine(
  key: string,
  namespace: string,
  locale: LayoutLocale,
): string | undefined {
  const v = renderTranslation(t(`hero.${key}`, namespace, locale));
  if (v == null) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

export function readHeroImages(
  namespace: string,
  locale: LayoutLocale,
): HeroImage[] {
  const raw = t('hero.hero_images', namespace, locale) as unknown;
  return Array.isArray(raw) ? raw.filter((img): img is HeroImage => isHeroImage(img)) : [];
}

export function readHeroCtas(namespace: string, locale: LayoutLocale): HeroCtaEntry[] {
  const raw = t('hero.hero_ctas', namespace, locale) as unknown;
  return Array.isArray(raw)
    ? raw.filter((item): item is HeroCtaEntry => isHeroCtaEntry(item))
    : [];
}

/** Optional trait list under `hero.hero_meta_items` (e.g. design-system page). */
export function readHeroMetaItems(namespace: string, locale: LayoutLocale): string[] {
  const raw = t('hero.hero_meta_items', namespace, locale) as unknown;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string' && x.trim() !== '');
}

export function readHeroMetaLabel(
  namespace: string,
  locale: LayoutLocale,
  fallback: string,
): string {
  const v = t('hero.hero_meta_label', namespace, locale);
  if (typeof v === 'string' && v.trim() && v !== 'hero.hero_meta_label') return v;
  return fallback;
}

/**
 * Optional multi-paragraph intro under `hero.hero_body` (string[]).
 * If absent, falls back to **`hero_description`** split on blank lines.
 */
export function readHeroBody(namespace: string, locale: LayoutLocale): string[] {
  const raw = t('hero.hero_body', namespace, locale) as unknown;
  if (Array.isArray(raw)) {
    return raw
      .map((x) => {
        const v = renderTranslation(x);
        if (v == null) return '';
        return String(v).trim();
      })
      .filter((s) => s !== '');
  }
  const desc = heroLine('hero_description', namespace, locale);
  if (desc == null) return [];
  const parts = desc
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : [desc];
}

export type HeroBgSources = { hero_bg: string; hero_bg_2x?: string };

/** Optional decorative hero background from `hero.hero_bg` + `hero.hero_bg_2x` in locale JSON. */
export function readHeroBg(namespace: string, locale: LayoutLocale): HeroBgSources | undefined {
  const hero_bg = heroLine('hero_bg', namespace, locale);
  if (!hero_bg) return undefined;
  const hero_bg_2x = heroLine('hero_bg_2x', namespace, locale);
  return { hero_bg, hero_bg_2x: hero_bg_2x || undefined };
}
